import { useState, useEffect } from 'react'
import {
  Mic,
  Download,
  Server,
  Play,
  Square,
  RotateCcw,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Copy,
  AlertCircle,
  Users,
  Headphones
} from 'lucide-react'
import { teamSpeakAPI, authAPI } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function VoiceChatPage() {
  const [status, setStatus] = useState(null)
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [copied, setCopied] = useState(null)

  useEffect(() => {
    const load = async () => {
      try {
        const [statusRes, clientsRes, userRes] = await Promise.all([
          teamSpeakAPI.getStatus(),
          teamSpeakAPI.getClients(),
          authAPI.getCurrentUser().catch(() => ({ data: { user: null } }))
        ])
        setStatus(statusRes.data)
        setClients(clientsRes.data.clients || [])
        setCurrentUser(userRes.data.user)
      } catch (err) {
        setError(err.response?.data?.error || 'Kan TeamSpeak status niet laden')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handlePower = async (action) => {
    setActionLoading(action)
    try {
      await teamSpeakAPI.powerAction(action)
      await new Promise(r => setTimeout(r, 2000))
      const res = await teamSpeakAPI.getStatus()
      setStatus(res.data)
    } catch (err) {
      setError(err.response?.data?.error || `${action} mislukt`)
    } finally {
      setActionLoading(null)
    }
  }

  const copyToClipboard = (text, key) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text))
    } else {
      fallbackCopy(text)
    }
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }

  const fallbackCopy = (text) => {
    const el = document.createElement('textarea')
    el.value = text
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.focus()
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }

  const isAdmin = ['admin','superadmin'].includes(currentUser?.role)
  const running = status?.running
  const primaryIP = status?.serverIPs?.[0] || status?.host || '192.168.0.174'

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-neon-green" />
        <span className="ml-3 text-muted-foreground">Laden...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-neon-green to-neon-blue flex items-center justify-center flex-shrink-0">
          <Headphones className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Voice Chat</h1>
          <p className="text-muted-foreground text-sm">TeamSpeak 3 spraakcommunicatie</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Server Status */}
        <div className="rounded-lg border border-neon bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-neon-green" />
              <h2 className="font-semibold text-lg">TeamSpeak 3 Server</h2>
            </div>
            {running ? (
              <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30">
                <CheckCircle className="h-3 w-3 mr-1" /> Online
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <XCircle className="h-3 w-3 mr-1" /> Offline
              </Badge>
            )}
          </div>

          {/* Connection info */}
          <div className="space-y-2">
            {status?.serverIPs?.map((ip, i) => (
              <div key={ip} className="flex items-center justify-between bg-background/50 rounded px-3 py-2">
                <div>
                  <span className="text-xs text-muted-foreground mr-2">IP {i + 1}</span>
                  <span className="font-mono text-sm">{ip}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-neon-green"
                  onClick={() => copyToClipboard(ip, `ip-${i}`)}
                >
                  {copied === `ip-${i}` ? <CheckCircle className="h-3.5 w-3.5 text-neon-green" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            ))}
            <div className="flex items-center justify-between bg-background/50 rounded px-3 py-2">
              <div>
                <span className="text-xs text-muted-foreground mr-2">Voice Port</span>
                <span className="font-mono text-sm">{status?.voicePort || 9987}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-neon-green"
                onClick={() => copyToClipboard(String(status?.voicePort || 9987), 'port')}
              >
                {copied === 'port' ? <CheckCircle className="h-3.5 w-3.5 text-neon-green" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>

          {/* Resource usage */}
          {status?.resources && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-background/50 rounded px-3 py-2">
                <p className="text-xs text-muted-foreground">CPU</p>
                <p className="font-mono">{status.resources.cpu?.toFixed(1) ?? '–'}%</p>
              </div>
              <div className="bg-background/50 rounded px-3 py-2">
                <p className="text-xs text-muted-foreground">RAM</p>
                <p className="font-mono">
                  {status.resources.memory
                    ? `${(status.resources.memory / 1024 / 1024).toFixed(0)} MB`
                    : '–'}
                </p>
              </div>
            </div>
          )}

          {/* Admin token */}
          {isAdmin && status?.adminToken && (
            <div className="bg-background/50 rounded px-3 py-2 space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">ServerAdmin Privilege Key</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-yellow-400 flex-1 break-all">{status.adminToken}</code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-yellow-400"
                  onClick={() => copyToClipboard(status.adminToken, 'token')}
                >
                  {copied === 'token' ? <CheckCircle className="h-3.5 w-3.5 text-yellow-400" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Gebruik dit token in TS3 om serveradmin rechten te krijgen: Rechtermuisklik op server → Rechten → Privilege Key gebruiken</p>
            </div>
          )}

          {/* Admin controls */}
          {isAdmin && (
            <div className="pt-2 border-t border-border/50 space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Beheer</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handlePower('start')}
                  disabled={!!actionLoading || running}
                  className="flex-1 bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30 disabled:opacity-40"
                >
                  {actionLoading === 'start' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  <span className="ml-1">Start</span>
                </Button>
                <Button
                  size="sm"
                  onClick={() => handlePower('stop')}
                  disabled={!!actionLoading || !running}
                  className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 disabled:opacity-40"
                >
                  {actionLoading === 'stop' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                  <span className="ml-1">Stop</span>
                </Button>
                <Button
                  size="sm"
                  onClick={() => handlePower('restart')}
                  disabled={!!actionLoading}
                  className="flex-1 bg-neon-blue/10 hover:bg-neon-blue/20 text-neon-blue border border-neon-blue/30 disabled:opacity-40"
                >
                  {actionLoading === 'restart' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  <span className="ml-1">Herstart</span>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Connection Guide */}
        <div className="rounded-lg border border-neon bg-card p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-neon-blue" />
            <h2 className="font-semibold text-lg">Verbinden</h2>
          </div>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-green/20 text-neon-green flex items-center justify-center text-xs font-bold">1</span>
              <span className="text-muted-foreground">Download en installeer de TeamSpeak 3 client (zie hieronder)</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-green/20 text-neon-green flex items-center justify-center text-xs font-bold">2</span>
              <span className="text-muted-foreground">Open TeamSpeak 3 en ga naar <strong className="text-foreground">Verbindingen → Verbinden</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-green/20 text-neon-green flex items-center justify-center text-xs font-bold">3</span>
              <div className="text-muted-foreground">
                <p>Voer het serveradres in:</p>
                <code className="text-neon-green font-mono text-xs bg-neon-green/10 px-2 py-0.5 rounded mt-1 inline-block">
                  {primaryIP}:{status?.voicePort || 9987}
                </code>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-green/20 text-neon-green flex items-center justify-center text-xs font-bold">4</span>
              <span className="text-muted-foreground">Kies een bijnaam en klik op <strong className="text-foreground">Verbinden</strong></span>
            </li>
          </ol>
        </div>
      </div>

      {/* Client Downloads */}
      <div className="rounded-lg border border-neon bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Download className="h-5 w-5 text-neon-green" />
          <h2 className="font-semibold text-lg">Client Downloads</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {clients.map((client) => (
            <a
              key={client.platform}
              href={client.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col gap-2 p-4 rounded-lg border border-border/50 bg-background/30 hover:border-neon-green/40 hover:bg-neon-green/5 transition-all group"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{client.platform}</span>
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-neon-green transition-colors" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{client.description}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}
